"use client"

import type { UseFormReturn } from "react-hook-form"
import type { z } from "zod"
import type { Dependency, FieldConfig } from "@/components/ui/auto-form/types"
import { useTranslations } from "next-intl"
import AutoForm from "@/components/ui/auto-form"
import { Button } from "@/components/ui/button"

type AutoFormComponentProps<SchemaType extends z.ZodObject<any, any>> = {
  formSchema: SchemaType
  fieldConfig: FieldConfig<z.infer<SchemaType>>
  onSubmit: (values: z.infer<SchemaType>) => void
  dependencies?: Dependency<z.infer<SchemaType>>[]
  submitButton?: React.ReactNode | null
  hideSubmitButton?: boolean
  initialValues?: Partial<z.infer<SchemaType>>
  onChange?: (values: Partial<z.infer<SchemaType>>, form: UseFormReturn<z.infer<SchemaType>>) => void
  layout?: "vertical" | "grid"
  columns?: number
  containerClassName?: string
}

export default function AutoFormComponent<SchemaType extends z.ZodObject<any, any>>({
  formSchema,
  fieldConfig,
  onSubmit,
  dependencies,
  submitButton,
  hideSubmitButton = false,
  initialValues,
  onChange,
  layout = "vertical",
  columns = 1,
  containerClassName,
}: AutoFormComponentProps<SchemaType>) {
  const t = useTranslations("generic")
  const hasSubmitButton = submitButton !== undefined && submitButton !== null
  const onValuesChange = (values: Partial<z.infer<SchemaType>>, form: UseFormReturn<z.infer<SchemaType>>) => {
    onChange?.(values, form)
  }
  
  // Si hideSubmitButton es true, no mostrar ningún botón
  if (hideSubmitButton) {
    return (
      <AutoForm
        columns={columns}
        containerClassName={containerClassName}
        dependencies={dependencies}
        fieldConfig={fieldConfig}
        formSchema={formSchema}
        layout={layout}
        onSubmit={onSubmit}
        onValuesChange={onValuesChange}
        values={initialValues}
      />
    )
  }
  
  return (
    <AutoForm
      columns={columns}
      containerClassName={containerClassName}
      dependencies={dependencies}
      fieldConfig={fieldConfig}
      formSchema={formSchema}
      layout={layout}
      onSubmit={onSubmit}
      onValuesChange={onValuesChange}
      values={initialValues}
    >
      {hasSubmitButton ? submitButton : <Button type="submit">{t("submit")}</Button>}
    </AutoForm>
  )
}
